<template>
  <div class="team-list-table">
    <el-table
      :data="rows"
      @row-click="onRowClick"
      @sort-change="doChangeSort"
      ref="table"
      row-key="id"
      v-loading="loading"
    >
      <!-- Name -->
      <el-table-column
        :resizable="false"
        :label="fields.name.label"
        :prop="fields.name.name"
        :width="280"
        sortable="custom"
      >
        <template slot-scope="scope">
          <div
            class="list__avatar"
            :style="{ backgroundImage: getTeamAvatar(scope.row) }"
          />
          <el-tooltip
            :disabled="!hasPermissionToAuditLogs"
            effect="dark"
            :content="scope.row.id"
            placement="bottom"
          >
            <span
              class="list__avatar__name"
              v-text="presenter(scope.row, 'name')"
            />
          </el-tooltip>
        </template>
      </el-table-column>

      <!-- Program -->
      <el-table-column 
        :label="fields.program.label" 
        :prop="fields.program.name" 
        :resizable="false"
      >
        <!-- <template slot-scope="scope">
          <app-i18n
            code="common.none"
            v-if="!teamPrograms(scope.row.programs) || !teamPrograms(scope.row.programs).length"
          />
          <span
            v-else-if="teamPrograms(scope.row.programs).length === 1"
            v-text="teamPrograms(scope.row.programs)[0].label"
          />
        </template> -->
        <template slot-scope="scope">
          <app-i18n
            code="common.none"
            v-if="!scope.row.program"
          />
          <span
            v-else
            v-text="scope.row.program.name"
          />
        </template>
      </el-table-column>

      <!-- Subprogram -->
      <el-table-column
        :label="fields.subprogram.label"
        :prop="fields.subprogram.name"
        :resizable="false"
        :width="140"
      >
        <!-- <template slot-scope="scope">
          <app-i18n
            code="common.none"
            v-if="!teamSubprograms(scope.row.programs) || !teamSubprograms(scope.row.programs).length"
          />
          <span
            v-else-if="teamSubprograms(scope.row.programs).length === 1"
            v-text="teamSubprograms(scope.row.programs)[0].label"
          />
        </template> -->
        <template slot-scope="scope">
          <app-i18n
            code="common.none"
            v-if="!scope.row.subprogram"
          />
          <span
            v-else
            v-text="scope.row.subprogram.name"
          />
        </template>
      </el-table-column>

      <!-- Players -->
      <el-table-column
        :label="fields.players.label"
        :prop="fields.players.name"
        :resizable="false"
      >
        <template slot-scope="scope">
          <app-i18n
            v-if="!scope.row.players || !scope.row.players.length"
            code="common.none"
          />
          <el-tooltip
            v-else-if="scope.row.players.length === 1"
            effect="dark"
            :content="scope.row.players[0].firstName +' '+ scope.row.players[0].lastName"
            placement="bottom"
          >
            <span>
              <span> {{ scope.row.players.length }} </span>
              <app-i18n code="entities.team.enumerators.player.one"/>
            </span>
          </el-tooltip>

          <el-tooltip
            v-else-if="scope.row.players.length > 1"
            effect="dark"
            :content="scope.row.players.map(player => player.firstName +' '+ player.lastName).join('\n')"
            placement="bottom"
          >
            <span>
              <span> {{scope.row.players.length}} </span>
              <app-i18n code="entities.team.enumerators.player.many"/>
            </span>
          </el-tooltip>
        </template>
      </el-table-column>

      <!-- Coaches -->
      <el-table-column 
        :resizable="false" 
        :label="fields.coaches.label" 
        :prop="fields.coaches.name"
      >
        <template slot-scope="scope">
          <app-i18n
            v-if="!scope.row.coaches || !scope.row.coaches.length"
            code="common.none"
          />
          <el-tooltip
            v-else-if="scope.row.coaches.length === 1"
            effect="dark"
            :content="scope.row.coaches[0].email"
            placement="bottom"
          >
            <span v-text="scope.row.coaches[0].firstName +' '+ scope.row.coaches[0].lastName"/>
          </el-tooltip>
          <el-tooltip
            v-else-if="scope.row.coaches.length > 1"
            effect="dark"
            :content="scope.row.coaches.map(coach => coach.firstName +' '+ coach.lastName).join('\n')"
            placement="bottom"
          >
            <app-i18n code="entities.coach.list.multipleCoaches" />
          </el-tooltip>
        </template>
      </el-table-column>

      <!-- Created At -->
      <el-table-column
        v-if="hasPermissionToAuditLogs"
        :label="fields.createdAt.label"
        :prop="fields.createdAt.name"
        :resizable="false"
        sortable="custom"
      >
        <template slot-scope="scope">{{ presenter(scope.row, 'createdAt') }}</template>
      </el-table-column>

      <!-- Actions -->
      <el-table-column
        :fixed="isMobile? undefined : 'right'"
        :resizable="false"
        align="center"
        width="140"
      >
        <template slot-scope="scope">
          <div class="table-actions">
            <!-- View -->
            <!-- <router-link :to="`/team/${scope.row.id}`">
              <el-button type="text">
                <app-i18n code="common.view"></app-i18n>
              </el-button>
            </router-link>-->
            
            <!-- Edit -->
            <router-link :to="`/team/${scope.row.id}/edit`" v-if="hasPermissionToEdit">
              <el-button circle icon="icon-pen" />
            </router-link>

            <!-- Destroy -->
            <el-button
              :disabled="destroyLoading"
              @click="doDestroyWithConfirm(scope.row.id)"
              circle
              icon="icon-trash"
              v-if="hasPermissionToAuditLogs"
            />
          </div>
        </template>
      </el-table-column>
    </el-table>

    <!-- Pagination -->
    <div class="el-pagination-wrapper">
      <el-pagination
        :current-page="pagination.currentPage || 1"
        :disabled="loading"
        :layout="paginationLayout"
        :total="count"
        @current-change="doChangePaginationCurrentPage"
        @size-change="doChangePaginationPageSize"
      ></el-pagination>
    </div>
  </div>
</template>

<script>
import { AuditLogPermissions } from '@modules/audit-log/audit-log-permissions';
import { TeamModel } from '@modules/team/team-model';
import { mapGetters, mapActions } from 'vuex';
import { TeamPermissions } from '@modules/team/team-permissions';
import { i18n } from '@shared/i18n';

const { fields } = TeamModel;

export default {
  name: 'app-team-list-table',

  mounted() {
    this.doFetch();
  },
  
  computed: {
    ...mapGetters({
      rows: 'team/list/rows',
      count: 'team/list/count',
      loading: 'team/list/loading',
      pagination: 'team/list/pagination',
      isMobile: 'layout/isMobile',
      currentUser: 'auth/currentUser',
      destroyLoading: 'team/destroy/loading',
      paginationLayout: 'layout/paginationLayout',
    }),

    hasPermissionToAuditLogs() {
      return new AuditLogPermissions(this.currentUser).read;
    },

    hasPermissionToEdit() {
      return new TeamPermissions(this.currentUser).edit;
    },

    hasPermissionToDestroy() {
      return new TeamPermissions(this.currentUser).destroy;
    },

    fields() {
      return fields;
    },

    teamPrograms() {
      return (programsObject) => {
        if (!programsObject) return [];
        return Object.keys(programsObject).map((id) => ({
          id,
          label: programsObject[id].label,
        }));
      };
    },

    teamSubprograms() {
      return (programsObject) => {
        const subprograms = [];

        if (programsObject) {
          Object.values(programsObject).forEach(
            (programObject) => {
              if (!('subprograms' in programObject)) return;
              if (!programObject.subprograms) return;
              Object.keys(
                programObject.subprograms,
              ).forEach((subprogramId) => {
                subprograms.push({
                  id: subprogramId,
                  label:
                    programObject.subprograms[subprogramId]
                      .label,
                });
              });
            },
          );
        }

        return subprograms;
      };
    },
  },

  methods: {
    ...mapActions({
      doChangeSort: 'team/list/doChangeSort',
      doChangePaginationCurrentPage:
        'team/list/doChangePaginationCurrentPage',
      doChangePaginationPageSize:
        'team/list/doChangePaginationPageSize',
      doDestroy: 'team/destroy/doDestroy',
      doFetch: 'team/list/doFetch',
    }),

    presenter(row, fieldName) {
      return TeamModel.presenter(row, fieldName);
    },

    async doDestroyWithConfirm(id) {
      try {
        await this.$confirm(
          i18n('common.areYouSure'),
          i18n('common.confirm'),
          {
            confirmButtonText: i18n('common.yes'),
            cancelButtonText: i18n('common.no'),
            type: 'warning',
          },
        );

        return this.doDestroy(id);
      } catch (error) {
        // no
      }
    },

    onRowClick(row, column) {
      if (!('label' in column)) return;
      this.$router.push({
        name: 'teamView',
        params: {
          id: row.id,
        }
      });
      // this.doManage(row);
    },

    getTeamAvatar(row) {
      if (!('logo' in row)) return null;
      if (!(row.logo instanceof Array)) return null;
      if (!row.logo.length) return null;
      return `url("${row.logo[0].publicUrl}")`;
    },
  },
};
</script>

